import { JSX } from 'react';
import {
  AquariumIcon,
  BlizzardIcon,
  BoilerIcon,
  CellPhoneIcon,
  ComputerIcon,
  DishwasherIcon,
  EvChargerIcon,
  FloodIcon,
  FridgeIcon,
  GarageIcon,
  HeatwaveIcon,
  HurricaneIcon,
  HvacIcon,
  LaundryIcon,
  LightBulbIcon,
  MedicalDeviceIcon,
  MicrowaveIcon,
  OvenIcon,
  RouterIcon,
  SecuritySystemIcon,
  SepticPumpIcon,
  SmokeDetectorIcon,
  SumpPumpIcon,
  TornadoIcon,
  WellPumpIcon,
  WildfireIcon,
} from '../../icons';

export interface OutageExperienceOption {
  id: string;
  label: string;
  description: string;
  icon: JSX.Element;
  defaultSelected: boolean;
}

export interface OutageExperienceOptions {
  eventConcerns: OutageExperienceOption[];
  homeAppliances: OutageExperienceOption[];
  homeSystems: OutageExperienceOption[];
  technologyElectronics: OutageExperienceOption[];
}

export type OutageExperienceCategory = keyof OutageExperienceOptions;

export const outageExperienceOptions: OutageExperienceOptions = {
  eventConcerns: [
    {
      id: 'hurricane',
      label: 'Hurricane',
      description:
        'Extended power loss, flooding, and limited access to emergency services',
      icon: <HurricaneIcon />,
      defaultSelected: false,
    },
    {
      id: 'flood',
      label: 'Flood',
      description:
        'Water intrusion, mold, and loss of critical systems like sump pumps',
      icon: <FloodIcon />,
      defaultSelected: false,
    },
    {
      id: 'wildfire',
      label: 'Wildfire',
      description:
        'Evacuation risks, poor air quality, and loss of HVAC and safety systems',
      icon: <WildfireIcon />,
      defaultSelected: false,
    },
    {
      id: 'heatwave',
      label: 'Heatwave',
      description: 'Unsafe indoor heat and loss of cooling systems',
      icon: <HeatwaveIcon />,
      defaultSelected: false,
    },
    {
      id: 'blizzard',
      label: 'Winter storm',
      description: 'Freezing temperatures, frozen pipes, and loss of heating',
      icon: <BlizzardIcon />,
      defaultSelected: false,
    },
    {
      id: 'tornado',
      label: 'Tornado',
      description:
        'Sudden outages, structural damage, and limited emergency access',
      icon: <TornadoIcon />,
      defaultSelected: false,
    },
  ],
  homeAppliances: [
    {
      id: 'fridge',
      label: 'Fridge/freezer',
      description: 'Food spoils quickly',
      icon: <FridgeIcon />,
      defaultSelected: true,
    },
    {
      id: 'oven',
      label: 'Electric stove/oven',
      description: 'No ability to cook meals',
      icon: <OvenIcon />,
      defaultSelected: true,
    },
    {
      id: 'microwave',
      label: 'Microwave',
      description: 'No reheating or cook meals',
      icon: <MicrowaveIcon />,
      defaultSelected: true,
    },
    {
      id: 'dishwasher',
      label: 'Dishwasher',
      description: 'Unsanitary dishes stack up',
      icon: <DishwasherIcon />,
      defaultSelected: true,
    },
    {
      id: 'laundry',
      label: 'Washer/dryer',
      description: 'Laundry backlog',
      icon: <LaundryIcon />,
      defaultSelected: true,
    },
    {
      id: 'garage',
      label: 'Garage door opener',
      description: 'Vehicle access blocked',
      icon: <GarageIcon />,
      defaultSelected: false,
    },
    {
      id: 'aquarium',
      label: 'Aquarium system',
      description: 'Fish and coral die',
      icon: <AquariumIcon />,
      defaultSelected: false,
    },
    {
      id: 'ev-charger',
      label: 'Electric vehicle charger',
      description: 'Unable to charge EV',
      icon: <EvChargerIcon />,
      defaultSelected: false,
    },
  ],
  homeSystems: [
    {
      id: 'hvac',
      label: 'HVAC system',
      description: 'Extreme temp exposure',
      icon: <HvacIcon />,
      defaultSelected: true,
    },
    {
      id: 'boiler',
      label: 'Furnace/boiler',
      description: 'Risk of frozen pipes',
      icon: <BoilerIcon />,
      defaultSelected: true,
    },
    {
      id: 'sump-pump',
      label: 'Sump pump',
      description: 'Basement can flood',
      icon: <SumpPumpIcon />,
      defaultSelected: false,
    },
    {
      id: 'well-pump',
      label: 'Well pump',
      description: 'Loss of running water',
      icon: <WellPumpIcon />,
      defaultSelected: false,
    },
    {
      id: 'septic',
      label: 'Septic pump',
      description: 'Sewage backup',
      icon: <SepticPumpIcon />,
      defaultSelected: false,
    },
    {
      id: 'medical',
      label: 'Medical devices',
      description: 'Life-safety threat',
      icon: <MedicalDeviceIcon />,
      defaultSelected: false,
    },
    {
      id: 'security',
      label: 'Security system',
      description: 'Increased risk of break-ins',
      icon: <SecuritySystemIcon />,
      defaultSelected: true,
    },
    {
      id: 'smoke',
      label: 'Fire/smoke detection',
      description: 'Increased risk of fire',
      icon: <SmokeDetectorIcon />,
      defaultSelected: true,
    },
  ],
  technologyElectronics: [
    {
      id: 'router',
      label: 'Internet router/modem',
      description: 'No access to news or work',
      icon: <RouterIcon />,
      defaultSelected: true,
    },
    {
      id: 'computer',
      label: 'Computer',
      description: 'Work stoppage',
      icon: <ComputerIcon />,
      defaultSelected: false,
    },
    {
      id: 'lighting',
      label: 'Lighting system',
      description: 'Total darkness',
      icon: <LightBulbIcon />,
      defaultSelected: true,
    },
    {
      id: 'phone',
      label: 'Cell phone chargers',
      description: 'Prevents communication',
      icon: <CellPhoneIcon />,
      defaultSelected: true,
    },
  ],
};

export const getDefaultOutageExperience = () => {
  const getDefaultIds = (options: OutageExperienceOption[]) =>
    options.filter((opt) => opt.defaultSelected).map((opt) => opt.id);

  return {
    eventConcerns: getDefaultIds(outageExperienceOptions.eventConcerns),
    homeAppliances: getDefaultIds(outageExperienceOptions.homeAppliances),
    homeSystems: getDefaultIds(outageExperienceOptions.homeSystems),
    technologyElectronics: getDefaultIds(
      outageExperienceOptions.technologyElectronics
    ),
  };
};
